<script setup lang="ts">
/* =============================================================
   COMPANIES — LIST
   =============================================================
   Name, country, what they have on the platform, and contact.
   The VAT number is on the detail page: it is reference data you
   look up when you need it, not something you scan a list by.

   The two counts come back as PostgREST embedded aggregates on
   the same request — see listCompaniesWithCounts — so the list
   costs one round trip and no extra rows. They sit before the
   contact column because an email is wide enough to push them
   off a narrow screen.

   Opening a company is the only thing a row does — edit and
   delete are on the detail page. Creating one here is still the
   only way a company comes into existence at all, since nothing
   in the product does it.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import FilterInput from '@/components/ui/FilterInput.vue'
import Icon from '@/components/Icon.vue'
import { routes, to } from '@/config/routes'
import { listCompaniesWithCounts } from '@/lib/companies'
import { useCountries } from '@/lib/countries'
import { DataError } from '@/lib/errors'
import type { CompanyWithCounts } from '@/lib/types'

const router = useRouter()
const { countryName } = useCountries()

const rows = ref<CompanyWithCounts[]>([])
const loading = ref(true)
const loadError = ref('')
const filter = ref('')

const visible = computed(() => {
  const q = filter.value.trim().toLowerCase()
  if (!q) return rows.value
  return rows.value.filter(
    (r) =>
      r.name.toLowerCase().includes(q) ||
      countryName(r.country_code).toLowerCase().includes(q) ||
      r.contact_email.toLowerCase().includes(q),
  )
})

const countLabel = computed(() => {
  const total = rows.value.length
  const shown = visible.value.length
  const noun = total === 1 ? 'company' : 'companies'
  return shown === total ? `${total} ${noun}` : `${shown} of ${total} ${noun}`
})

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    rows.value = await listCompaniesWithCounts()
  } catch (e) {
    loadError.value = e instanceof DataError ? e.message : 'Could not load companies.'
  } finally {
    loading.value = false
  }
}

onMounted(load)

/** See the note on the same function in BerthsPage. */
function openRow(company: CompanyWithCounts, event: MouseEvent): void {
  if ((event.target as HTMLElement).closest('a')) return
  if (window.getSelection()?.toString()) return
  void router.push(to.company(company.id))
}
</script>

<template>
  <PageHeader title="Companies" description="Organisations that own berths and operate vessels.">
    <template #actions>
      <Button variant="ghost" icon="refresh" :loading="loading" @click="load">Refresh</Button>
      <Button icon="plus" :to="routes.companyNew">New company</Button>
    </template>
  </PageHeader>

  <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>

  <Panel flush :title="rows.length > 0 ? countLabel : undefined">
    <template v-if="rows.length > 0" #actions>
      <FilterInput
        v-model="filter"
        label="Filter companies by name, country or contact"
        placeholder="Filter companies"
      />
    </template>

    <p v-if="loading && rows.length === 0" class="panel-status">Loading…</p>

    <EmptyState
      v-else-if="rows.length === 0"
      icon="building"
      title="No companies yet"
      description="Nothing in the product creates a company, so the first pilot customer is registered here."
    >
      <template #action>
        <Button icon="plus" :to="routes.companyNew">New company</Button>
      </template>
    </EmptyState>

    <p v-else-if="visible.length === 0" class="panel-status">No company matches “{{ filter }}”.</p>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Country</th>
            <th scope="col">Berths</th>
            <th scope="col">Vessels</th>
            <th scope="col">Contact</th>
            <th scope="col"><span class="sr-only">Open</span></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="company in visible"
            :key="company.id"
            class="rowLink"
            @click="openRow(company, $event)"
          >
            <td class="cell-strong">
              <RouterLink :to="to.company(company.id)" class="nameLink">
                {{ company.name }}
              </RouterLink>
            </td>
            <td class="cell-muted">{{ countryName(company.country_code) }}</td>
            <td class="cell-num">{{ company.berths }}</td>
            <td class="cell-num">{{ company.vessels }}</td>
            <td class="cell-muted">{{ company.contact_email }}</td>
            <td class="cell-actions">
              <Icon name="chevronRight" :size="16" class="chevron" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Panel>
</template>
